import React, { useEffect } from 'react';
import { graphql } from 'gatsby';
import Layout from '../../layout/Layout';
import { getParagraph } from '../../components/componentLookup';
import { ArticleHeading } from './ArticleHeading/ArticleHeading';
import { ArticleContent } from './ArticleContent/ArticleContent';
import { ArticleAside } from './ArticleAside/ArticleAside';
import { ArticleShare } from './ArticleShare/ArticleShare';
import { ArticleButtons } from './ArticleButtons/ArticleButtons';
import { cleanLink } from '../../shared/cleanLink';
import { Chip } from '../../components/Chip/Chip';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { SEO } from '../../layout/SEO';
import { processSchema } from '../../shared/Schema/Schema';
import { processMetatags } from '../../shared/metaTags';
import { makeAnalyticsCall } from '../../shared/analytics/callAnalytics';
import { EventName } from '../../models/GTMEventTypes';
import './Article.scss';

export interface ListicleItem {
    number: number;
    text: string;
    id: string;
}

export default function Article({ data, pageContext }) {
    enum ArticleBodyTypes {
        HEADING = `paragraph__article_heading`,
        IMAGE = `paragraph__embedded_image`,
        TEXT = `paragraph__text`,
        FORM = `paragraph__single_column_dynamic_form`,
    }
    const { nodeArticle: page } = data;
    const listicleItems: ListicleItem[] = [];
    const summary = page.relationships?.field_summary;
    const customSchemas = page.relationships?.field_schema;

    let heroImage;

    if (summary?.relationships?.field_image) {
        heroImage = () => (
            <GatsbyImage
                image={getImage(
                    summary.relationships.field_image.relationships
                        ?.field_media_image?.localFile
                )}
                alt={summary.relationships.field_image.field_media_image?.alt}
            />
        );
    }

    const paragraphs = page.relationships.field_article_body.map(
        (paragraph) => {
            if (summary.field_listicle) {
                if (paragraph.type === ArticleBodyTypes.HEADING) {
                    const listicleItem: ListicleItem = {
                        number: listicleItems.length + 1,
                        text: paragraph.field_heading,
                        id: `section-${cleanLink(paragraph.field_heading)}`,
                    };
                    listicleItems.push(listicleItem);
                    paragraph.listicleItem = listicleItem;
                }
            }

            return getParagraph(paragraph, true);
        }
    );
    const fieldComponents = page.relationships?.field_components?.map(
        (paragraph) => getParagraph(paragraph)
    );

    useEffect(() => {
        makeAnalyticsCall(EventName.PAGE_VIEW, {
            pageType: `Article`,
        });
        processSchema(customSchemas);
    }, []);

    return (
        <Layout>
            <ArticleHeading
                title={summary.field_article_title}
                subtitle={summary.field_subtitle}
                date={summary.field_publish_date}
                category={summary.relationships?.field_category?.name}
                listicle={summary.field_listicle}
                HeroImage={heroImage}
            />
            <div className="article-contents kite-container">
                <ArticleContent>
                    {listicleItems.length > 0 && (
                        <ol className="toc">
                            <h2>In this article:</h2>
                            {listicleItems.map((item, idx) => (
                                <li key={`toc-${idx}`}>
                                    <a href={`#${item.id}`}>{item.text}</a>
                                </li>
                            ))}
                        </ol>
                    )}
                    {paragraphs}
                    <div className="categories">
                        {summary.relationships?.field_category && (
                            <Chip>
                                {summary.relationships?.field_category?.name}
                            </Chip>
                        )}
                        {summary.relationships?.field_categories?.map(
                            (cat, idx) => (
                                <Chip key={`chip-${idx}`}>{cat.name}</Chip>
                            )
                        )}
                    </div>
                    <ArticleShare title={summary.field_article_title} />
                    <ArticleButtons
                        prev={pageContext.previous}
                        next={pageContext.next}
                    />
                </ArticleContent>
                <ArticleAside
                    relatedArticles={data.relatedArticles}
                    recentArticles={data.recentArticles}
                />
            </div>
            {fieldComponents}
        </Layout>
    );
}

export const Head = ({ data }) => {
    const { nodeArticle: page } = data;
    return (
        <>
            <SEO location={page.fields.url}>
                {processMetatags(page.metatag)}
            </SEO>
        </>
    );
};

export const query = graphql`
    query ($id: String!, $category: String) {
        nodeArticle(id: { eq: $id }) {
            fields {
                url
            }
            drupal_internal__nid
            drupal_internal__vid
            moderation_state
            metatag {
                tag
                attributes {
                    content
                    href
                    name
                    rel
                    property
                }
            }
            relationships {
                field_schema {
                    type: __typename
                    ...paragraph__custom_schema
                }
                field_summary {
                    field_article_title
                    field_subtitle
                    field_listicle
                    field_publish_date(formatString: "MMMM D, YYYY")
                    relationships {
                        field_category {
                            name
                        }
                        field_categories {
                            name
                        }
                        field_image {
                            relationships {
                                field_media_image {
                                    localFile {
                                        childImageSharp {
                                            ...ImageFullWidthSettings
                                        }
                                    }
                                }
                            }
                            field_media_image {
                                alt
                            }
                        }
                    }
                }
                field_article_body {
                    type: __typename
                    ...paragraph__article_heading
                    ...paragraph__article_links
                    ...paragraph__article_inline_callout
                    ...paragraph__article_sources_list
                    ...paragraph__embedded_image
                    ...paragraph__embedded_video
                    ...paragraph__single_column_dynamic_form
                    ...paragraph__text
                }
                field_components {
                    type: __typename
                    ...paragraph__article_sources_list
                    ...paragraph__components_anchor
                    ...paragraph__dynamic_case_study_preview
                    ...paragraph__embedded_image
                    ...paragraph__embedded_video
                    ...paragraph__four_column
                    ...paragraph__header_body_collection
                    ...paragraph__infographic
                    ...paragraph__quote
                    ...paragraph__recommended_articles
                    ...paragraph__single_column_dynamic_form
                    ...paragraph__single_col_custom_btns
                    ...paragraph__single_column_text
                    ...paragraph__three_column
                    ...paragraph__three_column_narrow
                    ...paragraph__two_column
                    ...paragraph__two_column_form_text
                    ...paragraph__two_column_text_image
                    ...paragraph__two_column_text_media
                }
            }
        }

        recentArticles: allNodeArticle(
            sort: {
                relationships: { field_summary: { field_publish_date: DESC } }
            }
            limit: 2
            filter: { id: { ne: $id } }
        ) {
            nodes {
                fields {
                    url
                }
                drupal_internal__nid
                drupal_internal__vid
                moderation_state
                relationships {
                    field_summary {
                        field_article_title
                        field_subtitle
                        relationships {
                            field_category {
                                name
                            }
                            field_image {
                                relationships {
                                    field_media_image {
                                        localFile {
                                            childImageSharp {
                                                ...ImageConstrainedOneHalfSettings
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        relatedArticles: allNodeArticle(
            sort: {
                relationships: { field_summary: { field_publish_date: DESC } }
            }
            limit: 2
            filter: {
                id: { ne: $id }
                relationships: {
                    field_summary: {
                        relationships: {
                            field_category: { name: { eq: $category } }
                        }
                    }
                }
            }
        ) {
            nodes {
                fields {
                    url
                }
                drupal_internal__nid
                drupal_internal__vid
                moderation_state
                relationships {
                    field_summary {
                        field_article_title
                        field_subtitle
                        relationships {
                            field_category {
                                name
                            }
                            field_image {
                                relationships {
                                    field_media_image {
                                        localFile {
                                            childImageSharp {
                                                ...ImageConstrainedOneHalfSettings
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;
